import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { IssueAssessmentStatus } from '@risksmart-app/domain/src/types/consts/issue-assessment-status';
import type {
  InsertIssueAssessmentMutation,
  InsertIssueAssessmentMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertIssueAssessmentDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type InsertIssueAssessmentTRPCInput = {
  ParentIssueId: string;
  Severity?: number | null;
  Status?: IssueAssessmentStatus | null;
  CertifiedIndividual?: string | null;
  IssueType?: string | null;
  ActualCloseDate?: string | null;
  TargetCloseDate?: string | null;
  PolicyOwnerCommentary?: string | null;
  PolicyOwner?: string | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  PolicyBreach?: boolean | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  Reportable?: boolean | null;
  PoliciesBreached?: string | null;
  Rationale?: string | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  IssueCausedByThirdParty?: boolean | null;
  SystemResponsible?: string | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  RegulatoryBreach?: boolean | null;
  RegulationsBreached?: string | null;
  ThirdPartyResponsible?: string | null;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  IssueCausedBySystemIssue?: boolean | null;
  CustomAttributeData?: CustomAttributeData | null;
  TagTypeIds: string[];
  DepartmentTypeIds: string[];
  RegulationsBreachedIds: string[];
  AssociatedControlIds: string[];
  PoliciesBreachedIds: string[];
};

export const useInsertIssueAssessment = createMutationHook({
  name: 'insertIssueAssessment',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.issueAssessment.insert.mutationOptions({ onSuccess }),
  // Coerce array union types (string | string[]) to string[] for tRPC
  mapInput: (
    variables: InsertIssueAssessmentMutationVariables
  ): InsertIssueAssessmentTRPCInput => ({
    ...variables,
    TagTypeIds: Array.isArray(variables.TagTypeIds)
      ? variables.TagTypeIds
      : [variables.TagTypeIds],
    DepartmentTypeIds: Array.isArray(variables.DepartmentTypeIds)
      ? variables.DepartmentTypeIds
      : [variables.DepartmentTypeIds],
    RegulationsBreachedIds: Array.isArray(variables.RegulationsBreachedIds)
      ? variables.RegulationsBreachedIds
      : [variables.RegulationsBreachedIds],
    AssociatedControlIds: Array.isArray(variables.AssociatedControlIds)
      ? variables.AssociatedControlIds
      : [variables.AssociatedControlIds],
    PoliciesBreachedIds: Array.isArray(variables.PoliciesBreachedIds)
      ? variables.PoliciesBreachedIds
      : [variables.PoliciesBreachedIds],
  }),
  invalidate: (trpc) => [
    trpc.frontend.issue.issueAssessmentByParentId.queryKey(),
    trpc.frontend.issue.issueById.queryKey(),
    // COI-471 parity: Apollo twin also evicts `control` and `tag_type`.
    trpc.frontend.control.pathKey(),
    trpc.frontend.tag.pathKey(),
  ],
  mapResponse: (result: { Id: string }): InsertIssueAssessmentMutation => ({
    insertChildIssueAssessment: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertIssueAssessmentDocument,
  graphqlVariables: (variables: InsertIssueAssessmentMutationVariables) => ({
    ...variables,
  }),
  evictFields: [
    'issue',
    'tag_type',
    'issue_assessment',
    'issue_parent',
    'control',
    'issue_assessment_aggregate',
    'issue_assessment_audit',
  ],
  refetchQueries: [namedOperations.Query.getIssueById],
});
